import { memo, useCallback } from 'react';

import { type FiltersTrackingLocation } from '@air/analytics';
import { Close, Plus, TriangleDown } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';

import {
  FiltersDropdownTrigger,
  type FiltersDropdownTriggerProps,
} from '~/components/Filters/FiltersButton/FiltersDropdownTrigger';
import {
  PrivateFiltersDropdown,
  type PrivateFiltersDropdownProps,
} from '~/components/Filters/FiltersButton/PrivateFiltersDropdown';
import { useFiltersDropdownController } from '~/components/Filters/FiltersDropdown/useFiltersDropdownController';
import { type CustomFieldFilterType, type FilterType } from '~/components/FiltersMenu/types';
import { useFiltersCountAppliedFromDropdown } from '~/hooks/filters/useFiltersCountAppliedFromDropdown';

export type MoreFiltersTriggerProps = {
  filters: FilterType[];
  customFields: CustomFieldFilterType[];
  trackLocation: FiltersTrackingLocation;
} & Pick<PrivateFiltersDropdownProps, 'availableTypes' | 'disabledFilters' | 'otherFilters'>;

export const MoreFiltersTrigger = memo(
  ({
    filters,
    customFields,
    availableTypes,
    otherFilters,
    disabledFilters,
    trackLocation,
  }: MoreFiltersTriggerProps) => {
    const { filtersCount } = useFiltersCountAppliedFromDropdown({
      filtersToCount: filters,
      customFieldsToCount: customFields,
    });
    const { clearSelectedFilters } = useFiltersDropdownController({
      availableFilters: filters,
      customFields,
      trackLocation,
    });
    const renderFiltersDropdown: FiltersDropdownTriggerProps['renderFiltersDropdown'] = useCallback(
      ({ closePopover }) => {
        return (
          <PrivateFiltersDropdown
            availableTypes={availableTypes}
            otherFilters={otherFilters}
            availableFilters={filters}
            customFields={customFields}
            title="More filters"
            onApplyClick={closePopover}
            trackLocation={trackLocation}
            disabledFilters={disabledFilters}
          />
        );
      },
      [availableTypes, customFields, disabledFilters, filters, otherFilters, trackLocation],
    );

    const renderTrigger: FiltersDropdownTriggerProps['renderTrigger'] = useCallback(
      ({ isActive, onClick }) => {
        return (
          <div className="relative shrink-0">
            <Button
              appearance={isActive || filtersCount ? 'outline' : 'ghost'}
              color={isActive || filtersCount ? 'blue' : 'grey'}
              className={filtersCount ? 'pr-5' : 'pr-2'}
              data-testid="MORE_FILTERS_TRIGGER"
              onClick={onClick}
              prefix={
                filtersCount ? (
                  <span
                    className="flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded-24 bg-blue-11 px-1 text-center font-bold text-10 text-blue-1"
                    data-testid="MORE_FILTERS_TRIGGER_COUNT"
                  >
                    {filtersCount}
                  </span>
                ) : (
                  <Plus />
                )
              }
              size="small"
              suffix={!filtersCount && <TriangleDown className="size-3" />}
            >
              More
            </Button>
            {!!filtersCount && (
              <IconButton
                appearance="ghost"
                className="absolute top-1/2 right-1 -translate-y-1/2"
                color={isActive || filtersCount ? 'blue' : 'grey'}
                data-testid="MORE_FILTERS_TRIGGER_CLOSE"
                icon={Close}
                label="Clear filters"
                onClick={clearSelectedFilters}
                size="extra-small"
              />
            )}
          </div>
        );
      },
      [clearSelectedFilters, filtersCount],
    );

    return <FiltersDropdownTrigger renderFiltersDropdown={renderFiltersDropdown} renderTrigger={renderTrigger} />;
  },
);

MoreFiltersTrigger.displayName = 'AdvancedFiltersTrigger';
